import React, { useMemo, useState } from "react";
import { ArrowUpRight, ArrowDownLeft, Link2, FileText, ChevronDown, AlertCircle, Zap } from "lucide-react";
import { extractWikiLinks, findMention, contextSnippet, linkAllMentions } from "@/lib/markdownUtils";

/**
 * Obsidian-style links pane for the active note:
 *  - Outgoing links: [[links]] this note points to (resolved + unresolved)
 *  - Backlinks: notes that link back here (with context snippet)
 *  - Unlinked mentions: notes that name this one in plain text — one-click to
 *    link ALL mentions in that note, or "Link all" across every note at once.
 */
export default function LinksPanel({ doc, docs, onOpen, onLink, onLinkAll }) {
  const { outgoing, backlinks, unlinked } = useMemo(() => {
    const byName = new Map();
    for (const d of docs) if (d.name) byName.set(d.name.toLowerCase(), d);

    const seen = new Set();
    const outgoing = [];
    for (const t of extractWikiLinks(doc.markdown)) {
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      outgoing.push({ name: t, target: byName.get(key) || null });
    }

    const backlinks = [];
    const unlinked = [];
    const nameLower = (doc.name || "").toLowerCase();
    for (const d of docs) {
      if (d.id === doc.id || !doc.name) continue;
      const links = extractWikiLinks(d.markdown).map((s) => s.toLowerCase());
      if (links.includes(nameLower)) {
        backlinks.push({ doc: d, snippet: contextSnippet(d.markdown, doc.name) });
      } else {
        const mention = findMention(d.markdown, doc.name);
        if (mention) {
          const { markdown: newMarkdown, count } = linkAllMentions(d.markdown, doc.name);
          unlinked.push({ doc: d, mention, count, newMarkdown });
        }
      }
    }
    return { outgoing, backlinks, unlinked };
  }, [doc, docs]);

  const totalUnlinked = unlinked.reduce((s, u) => s + u.count, 0);
  const linkAll = () => onLinkAll(unlinked.map((u) => ({ id: u.doc.id, markdown: u.newMarkdown })), totalUnlinked);

  return (
    <aside className="links-panel" data-testid="vault-links-panel">
      <LinkSection title="Backlinks" icon={ArrowDownLeft} count={backlinks.length} testid="backlinks-section">
        {backlinks.length === 0 ? (
          <div className="links-empty">No backlinks yet.</div>
        ) : (
          backlinks.map(({ doc: d, snippet }) => (
            <button key={d.id} className="links-item" onClick={() => onOpen(d.id)} data-testid={`backlink-${d.id}`}>
              <div className="links-item-name"><FileText size={12} /> {d.name}</div>
              {snippet && <div className="links-item-snippet">{snippet}</div>}
            </button>
          ))
        )}
      </LinkSection>

      <LinkSection title="Outgoing links" icon={ArrowUpRight} count={outgoing.length} testid="outgoing-section">
        {outgoing.length === 0 ? (
          <div className="links-empty">This note links to nothing yet.</div>
        ) : (
          outgoing.map(({ name, target }) => (
            <button
              key={name}
              className={`links-item ${target ? "" : "unresolved"}`}
              onClick={() => target && onOpen(target.id)}
              disabled={!target}
              data-testid={`outgoing-${name}`}
            >
              <div className="links-item-name">
                {target ? <FileText size={12} /> : <AlertCircle size={12} />} {name}
                {!target && <span className="links-ghost-tag">not created</span>}
              </div>
            </button>
          ))
        )}
      </LinkSection>

      <LinkSection
        title="Unlinked mentions"
        icon={Link2}
        count={unlinked.length}
        testid="unlinked-section"
        action={unlinked.length > 0 ? (
          <button className="links-link-all" onClick={linkAll} data-testid="link-all-btn" title="Link every mention across all notes">
            <Zap size={11} /> Link all ({totalUnlinked})
          </button>
        ) : null}
      >
        {unlinked.length === 0 ? (
          <div className="links-empty">No unlinked mentions.</div>
        ) : (
          unlinked.map(({ doc: d, mention, count, newMarkdown }) => (
            <div key={d.id} className="links-item links-unlinked" data-testid={`unlinked-${d.id}`}>
              <button className="links-unlinked-open" onClick={() => onOpen(d.id)}>
                <div className="links-item-name">
                  <FileText size={12} /> {d.name}
                  {count > 1 && <span className="links-ghost-tag">{count}×</span>}
                </div>
                {mention.snippet && <div className="links-item-snippet">{mention.snippet}</div>}
              </button>
              <button
                className="links-link-btn"
                onClick={() => onLink(d.id, newMarkdown, count)}
                data-testid={`link-mention-${d.id}`}
                title={count > 1 ? `Link all ${count} mentions in this note` : "Turn this mention into a link"}
              >
                <Link2 size={12} /> Link{count > 1 ? ` ${count}` : ""}
              </button>
            </div>
          ))
        )}
      </LinkSection>
    </aside>
  );
}

function LinkSection({ title, icon: Icon, count, children, testid, action }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="links-section" data-testid={testid}>
      <div className="links-section-head">
        <button className="links-head-toggle" onClick={() => setOpen((o) => !o)}>
          <ChevronDown size={13} className={`links-caret ${open ? "" : "closed"}`} />
          <Icon size={13} /> {title}
          <span className="links-count">{count}</span>
        </button>
        {action}
      </div>
      {open && <div className="links-section-body">{children}</div>}
    </div>
  );
}
